import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getEngine } from "@/lib/engineSingleton";
import { getRecSession, saveRecSession } from "@/lib/recStudioApi";
import { safeApiError } from "@/lib/safeErrorText";
import { applyMix, releasePart, syncSession } from "@/components/recstudio/sessionEngine";

const SAVE_MS = 900;
const body = (d) => ({ title: d.title, tracks: d.tracks, settings: d.settings, ...(d.lyrics ? { lyrics: d.lyrics } : {}), ...(d.instrumental ? { instrumental: d.instrumental } : { clear_instrumental: true }) });

/** One Recording Studio session: loads it, keeps the engine in step, and auto-saves every change. */
export default function useRecSession(sessionId) {
  const engine = getEngine();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [saveState, setSaveState] = useState("saved");
  const latest = useRef(null);
  const timer = useRef(null);

  const sync = useCallback(async (d, { quiet = false } = {}) => {
    setSyncing(true);
    try { if (await syncSession(engine, d) && !quiet) toast.message("Studio switched to Wet so you hear reverb and delay"); }
    catch (e) { toast.error("Some audio couldn't load", { description: e.message }); }
    finally { setSyncing(false); }
  }, [engine]);

  useEffect(() => {
    let live = true;
    getRecSession(sessionId).then((d) => { if (!live) return; latest.current = d; setDoc(d); sync(d, { quiet: true }); })
      .catch((e) => live && setError(e?.response?.status === 404 ? "That recording session isn't here." : safeApiError(e)));
    return () => { live = false; };
  }, [sessionId, sync]);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    if (!latest.current) return;
    setSaveState("saving");
    try { await saveRecSession(sessionId, body(latest.current)); setSaveState("saved"); return true; }
    catch (e) { setSaveState("error"); toast.error("Couldn't save the session", { description: safeApiError(e) }); return false; }
  }, [sessionId]);
  useEffect(() => () => { if (timer.current) { clearTimeout(timer.current); saveRecSession(sessionId, body(latest.current)).catch(() => {}); } }, [sessionId]);

  /** fn(doc) → next doc. `reload` re-syncs audio (new take / instrumental); otherwise only the mix is re-applied. */
  const change = useCallback((fn, { reload = false } = {}) => {
    const next = fn(latest.current);
    latest.current = next;
    setDoc(next);
    if (reload) sync(next);
    else if (applyMix(engine, next)) toast.message("Studio switched to Wet so you hear reverb and delay");
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(save, SAVE_MS);
  }, [engine, save, sync]);

  const updateTrack = useCallback((id, patch, opts) => change((d) => ({ ...d, tracks: d.tracks.map((t) => (t.id === id ? { ...t, ...patch } : t)) }), opts), [change]);
  const removeTrack = useCallback((id) => { releasePart(engine, sessionId, id); change((d) => ({ ...d, tracks: d.tracks.filter((t) => t.id !== id) })); }, [change, engine, sessionId]);

  return { doc, error, syncing, saveState, change, updateTrack, removeTrack, save, latest, engine };
}
